import { Badge } from '@/_shared/components/ui/badge';
import { cn } from '@/_shared/lib/utils';

/**
 * The audience lifecycle badge — shared by every surface that shows one (the detail header, the list card
 * + table, and the chat artifact card) so the label and colour are defined once.
 *
 * **It marks the exception, and renders NOTHING for a saved audience.** A saved audience is the normal
 * state, and a pill on the normal state is a pill on almost every row: it costs a slot on each line to
 * say what the absence of a Draft pill already says. Drafts are the rows a person needs to pick out, so
 * they are the rows that get a mark. This is the same rule the activations list follows — it badges a
 * disabled sync and leaves an enabled one unmarked.
 *
 * It also retires a contradiction. The status filter's options are 'Saved' and 'Draft'
 * (`lib/audienceList.ts`), while this badge read 'Active' for that same `saved` state — so filtering by
 * Saved returned a screenful of rows badged Active. There is now no second vocabulary to disagree with
 * the filter's.
 *
 * The state still reaches everyone: it is a filter facet, it is in each row's accessible name, and the
 * detail header's Save control states it directly.
 *
 * An out-of-date ("stale") size is a SEPARATE signal, carried by the count's own marker
 * ({@link StaleIndicator}) next to the number — NOT by this badge. A saved audience whose definition
 * changed since its last count is still saved; the stale marker is what flags "recalculate".
 */
export function AudienceStatusBadge({
  status,
  className,
}: {
  status: 'draft' | 'saved';
  className?: string;
}): React.ReactElement | null {
  // Callers render this bare (`{badges}` in `ListCard`, a flex child everywhere else), so returning null
  // leaves no empty wrapper and no stray `gap` behind — a flex gap needs two children to apply.
  if (status === 'saved') return null;
  return (
    <Badge variant="warning" bordered className={cn('shrink-0', className)}>
      Draft
    </Badge>
  );
}
